import { logger, shouldDisableLoader } from '@teambit/legacy.logger';
import type { CLIArgs, Command, Flags } from './command';
import { loader } from '@teambit/legacy.loader';
import { handleErrorAndExit } from './handle-errors';
import { TOKEN_FLAG_NAME, CACHE_ROOT } from '@teambit/legacy.constants';
import globalFlags from './global-flags';
import { Analytics } from '@teambit/legacy.analytics';
import type { OnCommandStartSlot } from './cli.main.runtime';
import pMapSeries from 'p-map-series';
import fs from 'fs-extra';
import path from 'path';

export const LAST_COMMAND_DETAILS_DIR = path.join(CACHE_ROOT, 'last-command-details');

type CommandResult = { data: any; exitCode: number };

export class CommandRunner {
  private commandName: string;
  constructor(
    private command: Command,
    private args: CLIArgs,
    private flags: Flags,
    private onCommandStartSlot: OnCommandStartSlot
  ) {
    this.commandName = parseCommandName(this.command.name);
  }

  /**
   * run command using one of the handler, "json"/"report"/"render". once done, exit the process.
   */
  async runCommand(shouldReturnResult = false): Promise<void | CommandResult> {
    try {
      this.bootstrapCommand();
      await this.invokeOnCommandStart();
      this.determineConsoleWritingDuringCommand();
      if (this.flags.json) {
        return await this.runJsonHandler(shouldReturnResult);
      }
      if (this.command.report) {
        return await this.runReportHandler(shouldReturnResult);
      }
      if (this.command.wait) {
        return await this.runWaitHandler();
      }
    } catch (err: any) {
      if (shouldReturnResult) throw err;
      return handleErrorAndExit(err, this.commandName);
    }

    throw new Error(`command "${this.commandName}" doesn't implement "render" nor "report" nor "wait" methods`);
  }

  private bootstrapCommand() {
    try {
      Analytics.init(this.commandName, this.flags, this.args);
    } catch (err: any) {
      // ignoring the error, we don't want to fail the app if analytics failed.
      logger.error('failed to initialize analytics', err);
    }
    logger.info(`[*] started a new command: "${this.commandName}" with the following data:`, {
      args: this.args,
      flags: this.flags,
    });
    const token = this.flags[TOKEN_FLAG_NAME];
    if (token) {
      globalFlags.token = token.toString();
    }
  }

  private async invokeOnCommandStart() {
    const funcs = this.onCommandStartSlot.values();
    await pMapSeries(funcs, (onCommandStart) => onCommandStart(this.commandName, this.args, this.flags));
  }

  /**
   * this works for both, Harmony commands and Legacy commands (the legacy-command-adapter
   * implements json() method)
   */
  private async runJsonHandler(shouldReturnResult = false): Promise<CommandResult | undefined> {
    if (!this.flags.json) return undefined;
    if (!this.command.json) throw new Error(`command "${this.commandName}" doesn't implement "json" method`);
    const result = await this.command.json(this.args, this.flags);
    const code = result.code || 0;
    const data = result.data || result;
    if (shouldReturnResult) return { data, exitCode: code };
    const isJsonStream = Boolean(this.flags.stream);
    if (isJsonStream) data.end = true;
    const jsonStr = isJsonStream ? `${JSON.stringify(data)}\n` : JSON.stringify(data, null, 2);
    await this.writeAndExit(jsonStr, code);
  }

  private async runReportHandler(shouldReturnResult = false): Promise<CommandResult | undefined> {
    if (!this.command.report) throw new Error('runReportHandler expects command.report to be implemented');
    const result = await this.command.report(this.args, this.flags);
    loader.off();
    const data = typeof result === 'string' ? result : result.data;
    const exitCode = typeof result === 'string' ? 0 : result.code;
    if (shouldReturnResult) return { data, exitCode };
    const details = typeof result === 'string' ? undefined : result.details;
    if (details) {
      await this.persistDetails(details).catch((err) => logger.error('failed to persist command details', err));
    }
    await this.writeAndExit(`${data}\n`, exitCode);
  }

  private async persistDetails(details: string) {
    await fs.ensureDir(LAST_COMMAND_DETAILS_DIR);
    const meta = JSON.stringify({ command: this.commandName, timestamp: new Date().toISOString() });
    await Promise.all([
      fs.writeFile(path.join(LAST_COMMAND_DETAILS_DIR, 'content'), details),
      fs.writeFile(path.join(LAST_COMMAND_DETAILS_DIR, 'meta.json'), meta),
    ]);
  }

  private async runWaitHandler() {
    if (!this.command.wait) throw new Error('runReportHandler expects command.wait to be implemented');
    await this.command.wait(this.args, this.flags);
  }

  /**
   * the loader and logger.console write output to the console during the command execution.
   * for internals commands, such as, _put, _fetch, the command.loader = false.
   */
  private determineConsoleWritingDuringCommand() {
    if (this.command.loader && !this.flags.json && !this.flags['get-yargs-completions'] && !shouldDisableLoader) {
      loader.on();
      loader.start(`running command "${this.commandName}"...`);
      logger.shouldWriteToConsole = true;
    } else {
      loader.off();
      logger.shouldWriteToConsole = false;
    }
  }

  private async writeAndExit(data: string, exitCode: number) {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    return process.stdout.write(data, async () => logger.exitAfterFlush(exitCode, this.commandName, data));
  }
}

export function parseCommandName(commandName: string): string {
  if (!commandName) return '';
  return commandName.split(' ')[0];
}
